import {
    IQueryPageRequest,
    IQuerySource,
    IWidgetSource,
    WidgetSourceType
} from "../core/reporting-models";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { QueryCommand } from "@features/filter";
import { EntityId } from "@bbwt/interfaces";
import { IM1DataSet } from "./im1-data-set";

export function getLayoutTypeName(code: LayoutType): string {
    switch (code) {
        case LayoutType.Cards:
            return "Cards";
        case LayoutType.Dividers:
            return "Dividers";
        case LayoutType.Plain:
            return "Plain";
    }
}

// Dashboard page models.
export interface IDashboard {
    id: string;
    name: string;
    description?: string;
    urlSlug?: string;
    createdOn: Date;

    // Foreign keys and navigational properties.
    ownerId?: string;
    ownerName?: string;
    isSystemDashboard: boolean;
    categories: ICategory[]
    defaultModules: string[];

    // NHS Settings
    iM1DailyAutoRefresh: boolean;
    dashboardIM1DataSets: IDashboardIM1DataSet[];
    showIM1RefreshButton: boolean;

    loadingMessageHtmlCode: string;
    loadingMessageCssRules: string;

    errorHandlingHtmlCode: string;
    errorHandlingCssRules: string;
}

// Dashboard build models.
export interface IDashboardBuild {
    id: string;
    name: string;
    displayName: boolean;
    description?: string;
    urlSlug?: string;
    style?: string;
    pdfHeaderHtmlCode?: string;
    pdfHeaderCssStyle?: string;
    pdfFooterHtmlCode?: string;
    pdfFooterCssStyle?: string;
    layout: LayoutType;
    widgetsMargin: number;
    widgetsPadding: number;
    isSystemDashboard: boolean;

    // Foreign keys and navigational properties.
    widgets: IDashboardWidgetBuild[];

    // NHS Settings
    iM1DailyAutoRefresh: boolean;
    dashboardIM1DataSets: IDashboardIM1DataSet[];
    showIM1RefreshButton: boolean;

    loadingMessageHtmlCode: string;
    loadingMessageCssRules: string;

    errorHandlingHtmlCode: string;
    errorHandlingCssRules: string;
}

export interface IDashboardQueryBuild {
    id: string;

    // Foreign keys and navigational properties.
    dashboardId: string;
    querySourceId: string;
    querySource?: IQuerySource;
}

export interface IDashboardWidgetBuild {
    id: string;
    rowIndex: number;
    columnIndex: number;

    // Foreign keys and navigational properties.
    widgetSourceId: string;
    widgetSource: IWidgetSource;
}

// Dashboard view models.
export interface IDashboardView {
    id: string;
    name: string;
    displayName: boolean;
    style?: string;
    pdfHeaderHtmlCode?: string;
    pdfHeaderCssStyle?: string;
    pdfFooterHtmlCode?: string;
    pdfFooterCssStyle?: string;
    layout: LayoutType;
    widgetsMargin: number;
    widgetsPadding: number;

    // Foreign keys and navigational properties.
    widgets: IDashboardWidgetView[];

    // NHS Settings
    iM1DailyAutoRefresh: boolean;
    dashboardIM1DataSets: IDashboardIM1DataSet[];
    showIM1RefreshButton: boolean;

    loadingMessageHtmlCode: string;
    loadingMessageCssRules: string;

    errorHandlingHtmlCode: string;
    errorHandlingCssRules: string;
}

export interface IDashboardQueryView {
    id: string;

    // Foreign keys and navigational properties.
    dashboardId: string;
    querySourceId: string;
    queryName: string;
    queryType: string;
    widgets: IWidgetSource[];
}

export interface IDashboardWidgetView {
    id: string;
    rowIndex: number;
    columnIndex: number;

    // Foreign keys and navigational properties.
    widgetSourceId: string;
    widgetType: WidgetSourceType;
}

export interface IDashboardMinimal {
    id: string;
    name: string;
}

export interface IDashboardQueryMinimal {
    id: string;

    // Foreign keys and navigational properties.
    querySourceId: string;
    queryName: string;
}

export interface DashboardQueryPageRequest {
    queryCommand?: QueryCommand;
    createdQueries: number[];
    removedQueries: number[];
    editedQueries: number[];
    dashboardWidgets: string[];
}

export interface ICategory {
    id: string;
    name: string;
    organisationsAvailable?: string[];
    dashboards?: IDashboard[]
}

export interface DashboardXlsExportingSettings {
    queryPageRequest: IQueryPageRequest;
    allowedWidgets: string[];
}

export enum LayoutType {
    Cards = 0,
    Dividers = 1,
    Plain = 2
}

export interface IModuleDefaultableDashboard {
    id: EntityId;
    name: string;
    modules: string[]
}

export interface IDashboardPdfTemplateSettings {
    format?: string;
    template: string;
    cssStyle?: string;
    headerHtmlCode: string;
    headerCssStyle: string;
    footerHtmlCode: string;
    footerCssStyle: string;
    widgetComponents: IWidgetComponent[][];
}

export interface IDashboardIM1DataSet {
    iM1DataSet: IM1DataSet;
    dashboardId: string;
}

export enum IM1UpdateResponseType {
    Im1DataUpdated = 1,
    Im1DataUpdatedDelayed = 2,
    Im1apiNotAvailable = 3,
    PatientNotExists = 4,
    DashboardNotExists = 5,
    PatientNotLinkedToIM1 = 6,
    InputCreationFailed = 7,
    PatientHasLimitedAccess = 8
}

export interface IM1UpdateResponse {
    success: boolean;
    im1UpdateResponseType: IM1UpdateResponseType;
    incompleteDateEntityTypes: IM1EntityType[];
}

export enum IM1EntityType {
    Allergy = 1,
    Immunization = 2,
    Prescription = 4,
    Episode = 8,
    Document = 16,
    Problem = 32,
    Investigation = 64,
}